import { useState } from 'react';

export type ClientId = 'hero' | 'core' | 'polaris';
export type Claims = Readonly<{
  aud: ClientId[];
  exp: number;
  iat: number;
  iss: string;
  nbf: number;
}>;
export type JWT = Readonly<{
  clientId: 'core';
  companyId: string;
  registeredClaims: Claims;
  scope: string[];
  userId: string;
  role: string;
}>;
export type Token = Readonly<{
  accessToken: string | null;
  refreshToken: string | null;
}>;

export const coreUserAccessToken = () => `${localStorage.getItem('user')?.toLowerCase()}-user-core-access-token`;
export const coreUserRefreshToken = () => `${localStorage.getItem('user')?.toLowerCase()}-user-core-refresh-token`;
export const companyUserAccessToken = 'company-core-access-token';

export const getUserToken = (username: string | null | undefined): Token | null => {
  if (username) {
    return getToken('core', 'user', username);
  }
  return null;
};

export const getToken = (clientId: ClientId, prefix: string, userId: string): Token | null => {
  const accessToken = localStorage.getItem(`${userId}-${prefix}-${clientId}-access-token`);
  const refreshToken = localStorage.getItem(`${userId}-${prefix}-${clientId}-refresh-token`);
  if (!accessToken || !refreshToken) {
    return null;
  }
  return {
    accessToken,
    refreshToken,
  };
};
export const useToken = () => {
  const [spoofToken, setSpoofToken] = useState<Token | null>(null);

  const addTokenForClientId = (clientId: ClientId, token: Token, prefix: string) => {
    if (prefix.includes('-user')) {
      if (token.accessToken) {
        localStorage.setItem(`${prefix}-${clientId}-access-token`, token.accessToken);
      }
      if (token.refreshToken) {
        localStorage.setItem(`${prefix}-${clientId}-refresh-token`, token.refreshToken);
      }
    } else {
      setSpoofToken(token);
      if (token.accessToken) {
        sessionStorage.setItem(companyUserAccessToken, token.accessToken);
      }
    }
  };

  const removeTokenForClientId = (clientId: ClientId, prefix: string) => {
    if (prefix === 'user') {
      const storageLength = localStorage.length;
      for (let i = 0; i < storageLength; i += 1) {
        const keyName = localStorage.key(i);
        if (keyName?.includes('access-token') || keyName?.includes('refresh-token')) {
          localStorage.removeItem(keyName);
        }
      }
    } else {
      setSpoofToken(null);
      sessionStorage.removeItem(companyUserAccessToken);
      localStorage.removeItem(companyUserAccessToken);
    }
  };

  return { addTokenForClientId, removeTokenForClientId, spoofToken };
};

export const decodeToken = (token: string): JWT => {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join('')
  );

  return JSON.parse(jsonPayload);
};
